import { Dispatch, FC, SetStateAction, useState } from "react";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { DairyIntervention } from "../../../../models/Intervention";
import { useFormContext } from "react-hook-form";
import {
  DsmCheckbox,
  DsmGrid,
  DsmIcon,
} from "@dsm-dcs/design-system-react";
import { CattleFeed } from "../../../../models/Intervention/CattleIntervention";
import {
  CheckboxChangeEvent,
  DsmCheckboxCustomEvent,
} from "@dsm-dcs/design-system";
import { Box, Tooltip } from "@material-ui/core";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { Spacer } from "../../../helpers/Spacer";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { FormType } from "../../common";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";

interface DairyV2FeedMothersMilkComponentProps {
  formType: FormType;
  fieldItemPrefix: string;
  currentStageData: CattleFeed;
  isMotherMilkEdit: boolean;
  setIsMotherMilkEdit: Dispatch<SetStateAction<boolean>>;
  resetEdit: () => void;
}

const DairyV2FeedMothersMilkComponent: FC<
  DairyV2FeedMothersMilkComponentProps
> = ({
  formType,
  fieldItemPrefix,
  currentStageData,
  isMotherMilkEdit,
  setIsMotherMilkEdit,
  resetEdit,
}) => {
    const intl = useIntl();
    const classes = processAndStageStylesV2() as CSSClassesList;
    const fc = useFormContext<DairyIntervention>();
    const barnOutputMassUnit = "kg DM";

    const defaultValue = () =>
      currentStageData.mothersMilk?.isMothersMilk ? "true" : "false";

    const [isMotherMilk, setIsMotherMilk] = useState<boolean>(
      !!currentStageData.mothersMilk?.isMothersMilk
    );

    const updateInputDisplay = (
      e: DsmCheckboxCustomEvent<CheckboxChangeEvent>
    ) => {
      const newState = e.target?.checked;
      fc.setValue(`${fieldItemPrefix}.isMothersMilk`, newState);
      setIsMotherMilk(newState);
    };

    const isCheckBoxDisabled = () => {
      if (
        formType === FormType.View ||
        currentStageData.mothersMilk?.isMothersMilk ||
        !isMotherMilkEdit
      )
        return true;
      return false;
    };

    return (
      <>
        <DsmGrid className={classes.dsmGridOneColumnWithBorder}>
          <Spacer />
          <div className={classes.resetButtonContainer}>
            <DsmCheckbox
              name={`${fieldItemPrefix}.isMothersMilk`}
              defaultValue={defaultValue()}
              checked={isMotherMilk}
              disabled={isCheckBoxDisabled()}
              onDsmChange={(e) => updateInputDisplay(e)}
              label={intl.formatMessage({
                id: `SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.MOTHERS_MILK_TO_CALVES`,
              })}
              style={{
                marginBottom: "16px",
              }}
            />
            <Tooltip
              title={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.MOTHERS_MILK_TO_CALVES",
              })}
              placement="left"
              className="ml-3"
              style={{ transform: "translateY(10%)" }}
            >
              <DsmIcon
                name="general/help-circle"
                className={classes.inlineTooltipClass}
                title=""
              />
            </Tooltip>
          </div>
          <Box />
          {isMotherMilk && !isMotherMilkEdit && (
            <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
              {isFieldFilled(fc.getValues(`${fieldItemPrefix}.amount`)) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.amount`}
                    label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT' })}
                    tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT.TOOLTIP' })}
                    baseline={currentStageData.mothersMilk?.amount}
                    metricUnit={barnOutputMassUnit}
                    displayTypeSelect={false}
                    type="number"
                    disabled
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalAmount`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT.TOOLTIP' })}
                  adornment={barnOutputMassUnit}
                  defaultValue={currentStageData.mothersMilk?.amount ?? ''}
                  type="number"
                  disabled
                />
              )}
               {isFieldFilled(fc.getValues(`${fieldItemPrefix}.dryMatterContent`)) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.dryMatterContent`}
                    label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT' })}
                    tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.TOOLTIP' })}
                    metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.ADORNMENT' })}
                    baseline={currentStageData.mothersMilk?.dryMatterContent}
                    displayTypeSelect={false}
                    type="number"
                    disabled
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalDryMatterContent`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.TOOLTIP' })}
                  adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.ADORNMENT' })}
                  placeholder={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.PLACEHOLDER' })}
                  defaultValue={currentStageData.mothersMilk?.dryMatterContent ?? ''}
                  type="number"
                  disabled
                />
              )}
              {isFieldFilled(fc.getValues(`${fieldItemPrefix}.crudeProteinContent`)) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.crudeProteinContent`}
                    label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT' })}
                    tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.TOOLTIP' })}
                    metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.ADORNMENT' })}
                    baseline={currentStageData.mothersMilk?.crudeProteinContent}
                    displayTypeSelect={false}
                    type="number"
                    disabled
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalCrudeProteinContent`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.TOOLTIP' })}
                  adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.ADORNMENT' })}
                  placeholder={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.PLACEHOLDER' })}
                  defaultValue={currentStageData.mothersMilk?.crudeProteinContent ?? ''}
                  type="number"
                  disabled
                />
              )}
              {isFieldFilled(fc.getValues(`${fieldItemPrefix}.digestibility`)) ? (
                <div>
                  <RowTextFieldWithMetrics
                    name={`${fieldItemPrefix}.digestibility`}
                    label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY' })}
                    tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.TOOLTIP' })}
                    metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.ADORNMENT' })}
                    baseline={currentStageData.mothersMilk?.digestibility}
                    displayTypeSelect={false}
                    type="number"
                    disabled
                  />
                  <Spacer />
                </div>
              ) : (
                <ReactHookDsmInput
                  name={`${fieldItemPrefix}.originalDigestibility`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.TOOLTIP' })}
                  adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.ADORNMENT' })}
                  placeholder={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.PLACEHOLDER' })}
                  defaultValue={currentStageData.mothersMilk?.digestibility ?? ''}
                  type="number"
                  disabled
                />
              )}
            </DsmGrid>
          )}
          {isMotherMilk && isMotherMilkEdit && (
            <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.amount`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.AMOUNT.TOOLTIP' })}
                  metricUnit={barnOutputMassUnit}
                  baseline={currentStageData.mothersMilk?.amount}
                  disabled={false}
                  type="number"
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.dryMatterContent`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.TOOLTIP' })}
                  metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DRY_MATTER_CONTENT.ADORNMENT' })}
                  baseline={currentStageData.mothersMilk?.dryMatterContent}
                  disabled={false}
                  type="number"
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.crudeProteinContent`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.TOOLTIP' })}
                  metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.CRUDE_PROTEIN_CONTENT.ADORNMENT' })}
                  baseline={currentStageData.mothersMilk?.crudeProteinContent}
                  disabled={false}
                  type="number"
                />
                <Spacer />
              </div>
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldItemPrefix}.digestibility`}
                  label={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY' })}
                  tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.TOOLTIP' })}
                  metricUnit={intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.FEED.MOTHERS_MILK.DIGESTIBILITY.ADORNMENT' })}
                  baseline={currentStageData.mothersMilk?.digestibility}
                  disabled={false}
                  type="number"
                />
                <Spacer />
              </div>
            </DsmGrid>)}
          {formType !== FormType.View &&
            <EditModeButtons
              isEditing={isMotherMilkEdit}
              onEdit={() => setIsMotherMilkEdit(true)}
              onCancel={resetEdit}
            />}
        </DsmGrid>
      </>
    );
  };

export default DairyV2FeedMothersMilkComponent;
